import { useState, useEffect } from 'react';
import { adminApi, type DepositRecord } from '@/lib/admin';
import { useToast } from '@/components/base/Toast';
import { SectionCard, StatusBadge, EmptyState, LoadingState, ErrorState, ConfirmDialog } from '@/pages/admin/components/UI';

const statusMaps = {
  pending: { label: '待确认', className: 'bg-gold-100 text-gold-700', icon: 'ri-time-line' },
  confirmed: { label: '已到账', className: 'bg-green-100 text-green-600', icon: 'ri-check-line' },
  rejected: { label: '已驳回', className: 'bg-red-100 text-red-500', icon: 'ri-close-line' },
  default: { label: '未知', className: 'bg-brown-100 text-brown-600', icon: 'ri-question-line' },
};

const tabs = [
  { key: 'all', label: '全部' },
  { key: 'pending', label: '待确认' },
  { key: 'confirmed', label: '已到账' },
  { key: 'rejected', label: '已驳回' },
];

export default function AdminDeposits() {
  const { showToast } = useToast();
  const [records, setRecords] = useState<DepositRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [tab, setTab] = useState('all');
  const [actionTarget, setActionTarget] = useState<DepositRecord | null>(null);
  const [actionType, setActionType] = useState<'confirm' | 'reject'>('confirm');
  const [confirmAmount, setConfirmAmount] = useState('');
  const [busy, setBusy] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    setError(false);
    try {
      const data = await adminApi.listDeposits();
      setRecords(data);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleAction = async () => {
    if (!actionTarget) return;
    setBusy(true);
    try {
      if (actionType === 'confirm') {
        const amount = confirmAmount ? parseFloat(confirmAmount) : actionTarget.amount;
        await adminApi.confirmDeposit(actionTarget.id, amount);
        showToast(`已确认 ¥${amount.toLocaleString('zh-CN', { maximumFractionDigits: 2 })} 到账`, 'success');
      } else {
        await adminApi.rejectDeposit(actionTarget.id);
        showToast('已驳回该笔入金', 'success');
      }
      setActionTarget(null);
      setConfirmAmount('');
      await fetchData();
    } catch (e) {
      showToast(e instanceof Error ? e.message : '操作失败', 'error');
    } finally {
      setBusy(false);
    }
  };

  const filtered = tab === 'all' ? records : records.filter((r) => r.status === tab);
  const pendingTotal = records.filter((r) => r.status === 'pending').reduce((s, r) => s + Number(r.amount), 0);

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-brown-900 text-lg font-bold">入金管理</h2>
          <p className="text-brown-400 text-xs mt-0.5">
            {records.length > 0
              ? `共 ${records.length} 笔 · 待确认 ¥${pendingTotal.toLocaleString('zh-CN', { maximumFractionDigits: 2 })}`
              : '暂无入金记录'}
          </p>
        </div>
        <div className="inline-flex bg-white rounded-lg border border-brown-100 p-1 self-start">
          {tabs.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-3 py-1.5 rounded-md text-sm font-medium cursor-pointer whitespace-nowrap transition-colors ${
                tab === t.key ? 'bg-gold-500 text-brown-950' : 'text-brown-500 hover:text-brown-700'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <SectionCard>
        {loading ? (
          <LoadingState />
        ) : error ? (
          <ErrorState onRetry={fetchData} />
        ) : filtered.length === 0 ? (
          <EmptyState title="暂无入金记录" desc="用户提交充值后，待确认的入金会出现在这里" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-brown-400 text-xs border-b border-brown-100">
                  <th className="px-4 md:px-5 py-3 font-medium">用户</th>
                  <th className="px-4 py-3 font-medium">金额</th>
                  <th className="px-4 py-3 font-medium hidden sm:table-cell">渠道</th>
                  <th className="px-4 py-3 font-medium hidden sm:table-cell">付款信息</th>
                  <th className="px-4 py-3 font-medium">状态</th>
                  <th className="px-4 py-3 font-medium hidden md:table-cell">时间</th>
                  <th className="px-4 py-3 font-medium text-right">操作</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.id} className="border-b border-brown-50 last:border-0 hover:bg-brown-50/40 transition-colors">
                    <td className="px-4 md:px-5 py-3.5">
                      <p className="text-brown-900 font-medium">{r.phone || '—'}</p>
                      <p className="text-brown-400 text-xs">#{r.id}</p>
                    </td>
                    <td className="px-4 py-3.5 text-gold-700 font-bold">
                      ¥{Number(r.amount).toLocaleString('zh-CN', { maximumFractionDigits: 2 })}
                    </td>
                    <td className="px-4 py-3.5 hidden sm:table-cell text-brown-700">
                      <p className="text-brown-700">{r.channel || '—'}</p>
                      {r.channel_receiver && (
                        <p className="text-gold-600 text-xs mt-0.5">收款: {r.channel_receiver}</p>
                      )}
                      {r.account && <span className="text-brown-400 text-xs block">{r.account}</span>}
                    </td>
                    <td className="px-4 py-3.5 hidden sm:table-cell text-brown-700 max-w-[220px]">
                      {r.payer_name ? (
                        <div>
                          <p className="text-brown-700 text-xs">{r.payer_name}</p>
                          {r.bank_name && <p className="text-brown-400 text-xs">{r.bank_name}</p>}
                          {r.remark && <p className="text-brown-400 text-xs">{r.remark}</p>}
                        </div>
                      ) : r.screenshots && r.screenshots.length > 0 ? (
                        <div className="flex gap-1.5">
                          {r.screenshots.map((url, idx) => (
                            <a key={idx} href={url} target="_blank" rel="noopener noreferrer" className="relative w-12 h-12 rounded-lg overflow-hidden border border-brown-200 flex-shrink-0">
                              <img src={url} alt={`截图${idx + 1}`} className="w-full h-full object-cover" />
                            </a>
                          ))}
                        </div>
                      ) : (
                        <span className="text-brown-400 text-xs">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3.5">
                      <StatusBadge status={r.status} maps={statusMaps} />
                    </td>
                    <td className="px-4 py-3.5 hidden md:table-cell text-brown-500 whitespace-nowrap">
                      {r.create_time || '—'}
                    </td>
                    <td className="px-4 py-3.5 text-right whitespace-nowrap">
                      {r.status === 'pending' ? (
                        <div className="inline-flex items-center gap-2">
                          <button
                            onClick={() => {
                              setActionType('confirm');
                              setConfirmAmount(String(r.amount || 0));
                              setActionTarget(r);
                            }}
                            className="px-3 py-1.5 rounded-md bg-green-500 text-white text-xs font-medium cursor-pointer whitespace-nowrap hover:bg-green-600 transition-colors"
                          >
                            确认到账
                          </button>
                          <button
                            onClick={() => {
                              setActionType('reject');
                              setActionTarget(r);
                            }}
                            className="px-3 py-1.5 rounded-md border border-brown-200 text-brown-500 text-xs cursor-pointer whitespace-nowrap hover:bg-brown-50 transition-colors"
                          >
                            驳回
                          </button>
                        </div>
                      ) : (
                        <span className="text-brown-300 text-xs">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>

      <ConfirmDialog
        open={!!actionTarget}
        title={actionType === 'confirm' ? '确认该笔入金到账？' : '确认驳回该笔入金？'}
        desc={
          actionType === 'confirm'
            ? `将为 ${actionTarget?.phone || '该用户'} 的账户增加余额。`
            : '驳回后该笔入金将标记为已驳回。'
        }
        confirmText={actionType === 'confirm' ? '确认到账' : '确认驳回'}
        danger={actionType === 'reject'}
        loading={busy}
        onCancel={() => {
          setActionTarget(null);
          setConfirmAmount('');
        }}
        onConfirm={handleAction}
      >
        {actionType === 'confirm' && actionTarget && (
          <div>
            <label className="block text-brown-700 text-xs font-medium mb-1.5">客户入账金额（元）</label>
            <input
              type="number"
              inputMode="decimal"
              value={confirmAmount}
              onChange={(e) => setConfirmAmount(e.target.value)}
              className="w-full text-sm text-brown-900 border border-brown-200 rounded-lg px-3 py-2.5 bg-white outline-none focus:border-gold-500 transition-colors"
              placeholder="请输入入账金额"
            />
            {actionTarget.amount > 0 && (
              <p className="text-brown-400 text-xs mt-1.5">
                原申请金额：¥{Number(actionTarget.amount).toLocaleString('zh-CN', { maximumFractionDigits: 2 })}
              </p>
            )}
          </div>
        )}
      </ConfirmDialog>
    </div>
  );
}